import { observer, useLocalObservable } from 'mobx-react';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import IconButton, { ICON_BUTTON_TYPES } from '../../../../components/buttons/IconButton';
import { COLUMN_LABEL_BY_KEY } from '../constants/columnFilterTypes';
import { cloneFilterValue, isEmptyFilterValue } from '../constants/filterValue';
import FilterEditorSwitch from './FilterEditorSwitch';

/**
 * Anchored inline popover that opens beneath a column-header ⌄ button.
 *
 * Follows the same pattern as the existing view-menu-wrap / sort-menu-wrap
 * dropdowns in SearchResults/index.jsx — conditionally rendered next to the
 * trigger button inside a `position: relative` ancestor. Outside-click and ESC
 * close the popover (the close handler is owned here, so the column-header
 * markup stays minimal).
 *
 * Draft state lives locally — the user edits freely and commits with Apply.
 * Apply with an empty draft is equivalent to Clear, so analysts can blank out
 * a filter in either UI without breaking the parent's expectations.
 *
 * Used by every filterable column header. Dispatch to the right inner editor
 * lives in FilterEditorSwitch (shared with the Add-filter panel) — now including
 * the keyword-cloud editor for Industry / Value chain / Products, previously the
 * unbuilt "V2" branch. Key end-markets stays unregistered, so it won't render.
 */
const ColumnFilterPopover = observer(
  ({ columnKey, currentValue, results, columnFilters, activeSpotlights, onApply, onClear, onClose }) => {
    const popoverRef = useRef(null);

    // Local "draft" state: clone the current value so the user can edit
    // without mutating the committed parent state. Sets are cloned manually.
    const draftState = useLocalObservable(() => ({
      draft: cloneFilterValue(currentValue),
      setDraft: (next) => {
        draftState.draft = next;
      }
    }));

    // Re-sync the draft if the committed value changes underneath an open
    // popover (e.g. a Spotlight toggle writes the related column filter).
    // `currentValue` is a stable reference while the user edits — it only
    // changes on an external commit — so this never clobbers in-progress
    // edits, but it stops Apply from writing back a stale draft.
    useEffect(() => {
      draftState.setDraft(cloneFilterValue(currentValue));
    }, [currentValue, draftState]);

    // Outside-click → close. Mirrors the pattern in `ProfilerSaveSearchPopup`
    // (capture-phase `click` listener + an explicit exemption for the trigger
    // button by class), so toggling open→closed by re-clicking the column
    // header ⌄ works correctly — without the exemption, the listener would
    // close first and the trigger's onClick would re-open it.
    useEffect(() => {
      const onClick = (e) => {
        if (!popoverRef.current) return;
        if (popoverRef.current.contains(e.target)) return;
        // Any column-filter trigger click is handled by its own onClick;
        // don't double-close here or we race the re-open.
        if (e.target?.closest?.('.column-filter-trigger')) return;
        onClose();
      };
      document.addEventListener('click', onClick, { capture: true });
      return () => document.removeEventListener('click', onClick, { capture: true });
    }, [onClose]);

    // ESC → close. Matches existing keyboard-shortcut patterns in this page.
    // Listen on document (not window) — same DOM event target, more
    // idiomatic for keyboard shortcuts and avoids the lint rule that
    // prefers globalThis over window in the SSR-safe sense.
    useEffect(() => {
      const onKey = (e) => {
        if (e.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', onKey, { capture: true });
      return () => document.removeEventListener('keydown', onKey, { capture: true });
    }, [onClose]);

    const label = COLUMN_LABEL_BY_KEY[columnKey] ?? columnKey;

    const handleApply = () => {
      if (isEmptyFilterValue(draftState.draft)) {
        onClear();
      } else {
        onApply(draftState.draft);
      }
      onClose();
    };

    const handleClear = () => {
      onClear();
      onClose();
    };

    // Not a true modal dialog (no focus trap, no backdrop). Sonar's
    // accessibility rule would push us toward the native `<fieldset>` /
    // `<details>` / `<address>` elements for `role="group"`, but each
    // brings its own default rendering quirks (`<fieldset>` requires a
    // `<legend>` first-child for the label, `<details>` adds a disclosure
    // arrow). The simpler fix: drop the explicit `role` and keep just
    // `aria-label` — screen readers still announce the region by its
    // label, and the column-header `⌄` trigger already announces
    // "Filter {Label}" on focus for context.
    return (
      <div className="column-filter-popover" ref={popoverRef} aria-label={`Filter ${label}`}>
        <div className="column-filter-popover-title">Filter {label}</div>

        {/* V1.3 — wrap the editable body + Apply button in a <form> so pressing
            Enter inside any text/number input submits to the Apply handler
            (browser-native behavior for forms with a single submit button). */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleApply();
          }}
        >
          <FilterEditorSwitch
            columnKey={columnKey}
            value={draftState.draft}
            onChange={draftState.setDraft}
            results={results}
            columnFilters={columnFilters}
            activeSpotlights={activeSpotlights}
          />

          <div className="column-filter-popover-actions">
            <IconButton
              type={ICON_BUTTON_TYPES.DEFAULT}
              innerText="Clear"
              withBorder={true}
              onClick={handleClear}
              disabled={isEmptyFilterValue(currentValue) && isEmptyFilterValue(draftState.draft)}
            />
            {/* The IconButton renders a <button type="button"> by default;
                we need type="submit" here so Enter / form-submit triggers
                Apply via the surrounding <form>. No onClick — a click on a
                submit button already fires the form's onSubmit; handling it
                here too would run handleApply twice. */}
            <button type="submit" className="column-filter-popover-apply">
              Apply
            </button>
          </div>
        </form>
      </div>
    );
  }
);

ColumnFilterPopover.propTypes = {
  columnKey: PropTypes.string.isRequired,
  currentValue: PropTypes.object,
  results: PropTypes.array.isRequired,
  columnFilters: PropTypes.object,
  activeSpotlights: PropTypes.object,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ColumnFilterPopover;
